import React, { useState, useEffect } from 'react';
import { X, Calculator, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export function BlogCTAPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('blogCTADismissed');

    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('blogCTADismissed', 'true');
  };

  const handleAIEstimate = () => {
    handleDismiss();
    const aiButton = document.querySelector('.fixed.bottom-4') as HTMLButtonElement;
    if (aiButton) aiButton.click();
  };

  if (isDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleDismiss}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.4, type: "spring", damping: 25 }}
            className="w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 rounded-2xl shadow-2xl border border-zinc-700/50 overflow-hidden relative">
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-200 p-2 hover:bg-zinc-800/50 rounded-lg transition-colors z-10"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="p-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400/10 rounded-full mb-4">
                    <Calculator className="h-8 w-8 text-yellow-400" />
                  </div>
                  <h2 className="text-2xl font-light text-zinc-100 mb-2">
                    Ready to Start Your Project?
                  </h2>
                  <p className="text-zinc-400 text-sm">
                    Get an instant AI estimate or speak with our experts
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={handleAIEstimate}
                    className="w-full group inline-flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-zinc-900 rounded-xl hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <Calculator className="h-5 w-5 relative z-10" />
                    <div className="relative z-10 text-left">
                      <div className="text-sm font-medium">Get AI Estimate</div>
                      <div className="text-xs opacity-80">Takes less than 1 minute</div>
                    </div>
                  </button>

                  <Link
                    to="/consultation"
                    onClick={handleDismiss}
                    className="w-full group inline-flex items-center justify-center gap-3 px-6 py-4 bg-zinc-800/50 backdrop-blur-sm border border-yellow-400/30 text-yellow-400 rounded-xl hover:bg-yellow-400 hover:text-zinc-900 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                    <Phone className="h-5 w-5 relative z-10" />
                    <div className="relative z-10 text-left">
                      <div className="text-sm font-medium">Free Consultation</div>
                      <div className="text-xs opacity-80">Speak with an expert</div>
                    </div>
                  </Link>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-zinc-500 text-xs mb-2">Or call us directly:</p>
                  <a
                    href="tel:9149606433"
                    className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium text-lg"
                  >
                    (914) 960-6433
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
